/**
 * The soft halos of lanterns, candles and the hearth (lantern-water's
 * `world.js` `glow`: an additive `THREE.Sprite` with the glow texture, tinted
 * `#ffd899`, at its own size and opacity). Here every halo of a scene is one
 * instance of one camera-facing additive quad mesh (the renderer's rule: no
 * `THREE.Sprite`; and one draw, not one per halo). The prefabs leave marker
 * objects (`ctx.glow`); `collectGlows` reads their world positions after the
 * scene is placed. Depth-tested, never depth-written, like the sprites.
 */
import * as THREE from 'three';
import { sceneLook } from './constants.ts';

const vertexShader = `attribute float glowOpacity;varying vec2 vUv;varying float vOpacity;
void main(){
  vUv=uv;vOpacity=glowOpacity;
  vec4 center=modelMatrix*instanceMatrix*vec4(0.,0.,0.,1.);
  float size=length(instanceMatrix[0].xyz);
  vec4 mv=viewMatrix*center;
  mv.xy+=position.xy*size;
  gl_Position=projectionMatrix*mv;
}`;

const fragmentShader = `uniform sampler2D map;uniform vec3 color;varying vec2 vUv;varying float vOpacity;
void main(){
  vec4 t=texture2D(map,vUv);
  gl_FragColor=vec4(color*t.rgb*t.a*vOpacity,1.);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}`;

export type Glows = { readonly mesh: THREE.InstancedMesh | null; readonly count: number; dispose(): void };

/** One instanced draw of every glow marker under `root` (removing the markers). */
export const collectGlows = (root: THREE.Object3D, map: THREE.Texture): Glows => {
  root.updateMatrixWorld(true);
  const markers: { readonly at: THREE.Vector3; readonly size: number; readonly opacity: number }[] = [];
  const remove: THREE.Object3D[] = [];
  root.traverse((o) => {
    const g = o.userData['glow'] as { readonly size: number; readonly opacity: number } | undefined;
    if (g === undefined) return;
    markers.push({ at: new THREE.Vector3().setFromMatrixPosition(o.matrixWorld), size: g.size, opacity: g.opacity });
    remove.push(o);
  });
  for (const o of remove) o.removeFromParent();
  if (markers.length === 0) return { mesh: null, count: 0, dispose: () => undefined };
  const geometry = new THREE.PlaneGeometry(1, 1);
  const opacity = new THREE.InstancedBufferAttribute(Float32Array.from(markers, (m) => m.opacity), 1);
  geometry.setAttribute('glowOpacity', opacity);
  const material = new THREE.ShaderMaterial({
    uniforms: { map: { value: map }, color: { value: new THREE.Color(sceneLook.materials.glowColor) } },
    vertexShader,
    fragmentShader,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
  const mesh = new THREE.InstancedMesh(geometry, material, markers.length);
  mesh.name = 'glows';
  mesh.frustumCulled = false;
  const m = new THREE.Matrix4();
  markers.forEach((g, i) => {
    mesh.setMatrixAt(i, m.makeScale(g.size, g.size, g.size).setPosition(g.at));
  });
  mesh.instanceMatrix.needsUpdate = true;
  return {
    mesh,
    count: markers.length,
    dispose() {
      mesh.removeFromParent();
      mesh.dispose();
      geometry.dispose();
      material.dispose();
    },
  };
};
